import { ReactNode, useState } from "react";
import {
  BaseModal,
  BaseModalBody,
  BaseModalFooter,
  BaseModalHeader,
  Checkbox,
  Loading,
  Tooltip,
} from "@src/common/components";
import { useRevertStopCompletionMutation } from "@api/graphql/generated/generated-types";
import StopCard from "@src/common/components/StopCard/StopCard";
import CourieButton from "@src/common/components/Button/Button";
import { compareStopsForDisplay } from "@src/shipments/utils/stopOrdering";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useAuth } from "@src/common/hooks/useAuth";
import { useErrorToast } from "@src/common/hooks/useErrorToast";
import {
  RevertStopCompletionModalShipment,
  RevertStopCompletionModalStop,
  useRevertStopCompletionModalShipment,
} from "./hooks/useRevertStopCompletionModalShipment";

interface RevertStopCompletionModalProps {
  isModalOpen: boolean;
  shipmentId: string;
  // The stop the dispatcher opened the action from; starts checked when it is
  // completed, ignored otherwise.
  preselectedStopId?: string;
  onClose: () => void;
  onSuccess: () => void;
}

function RevertStopCompletionModal({
  isModalOpen,
  shipmentId,
  preselectedStopId,
  onClose,
  onSuccess,
}: RevertStopCompletionModalProps) {
  const { courierId } = useAuth();
  const { shipment, loading, error, errorMessage } =
    useRevertStopCompletionModalShipment({
      courierId,
      shipmentId,
      skip: !isModalOpen,
    });

  if (!isModalOpen) return null;

  if (error || (!loading && !shipment))
    return (
      <RevertStopCompletionModalShell onClose={onClose}>
        <div className="py-8 text-center text-sm text-gray-600 dark:text-gray-300">
          {errorMessage ?? "Could not load order. Try again or close this dialog."}
        </div>
      </RevertStopCompletionModalShell>
    );

  if (loading || !shipment)
    return (
      <RevertStopCompletionModalShell onClose={onClose}>
        <div className="flex justify-center py-12">
          <Loading loading />
        </div>
      </RevertStopCompletionModalShell>
    );

  return (
    <RevertStopCompletionModalContent
      shipment={shipment}
      preselectedStopId={preselectedStopId}
      onClose={onClose}
      onSuccess={onSuccess}
    />
  );
}

function RevertStopCompletionModalShell({
  onClose,
  children,
}: {
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <BaseModal isOpen onClose={onClose} closeable size="default">
      <BaseModalHeader>Revert Completed Stops</BaseModalHeader>
      <BaseModalBody>{children}</BaseModalBody>
    </BaseModal>
  );
}

interface RevertStopCompletionModalContentProps {
  shipment: RevertStopCompletionModalShipment;
  preselectedStopId?: string;
  onClose: () => void;
  onSuccess: () => void;
}

function RevertStopCompletionModalContent({
  shipment,
  preselectedStopId,
  onClose,
  onSuccess,
}: RevertStopCompletionModalContentProps) {
  const { courierId } = useAuth();
  const showError = useErrorToast();
  const orderedStops: RevertStopCompletionModalStop[] = [
    ...shipment.stops,
  ].sort(compareStopsForDisplay);
  const completedStops: RevertStopCompletionModalStop[] = orderedStops.filter(
    (stop) => stop.completedAt
  );
  const [selectedStopIds, setSelectedStopIds] = useState<string[]>(() =>
    completedStops.some((stop) => stop.id === preselectedStopId)
      ? [preselectedStopId as string]
      : []
  );

  const [revertStopCompletion, { loading: revertStopCompletionLoading }] =
    useRevertStopCompletionMutation();

  const handleToggleStop = (stopId: string) => {
    setSelectedStopIds((prev) =>
      prev.includes(stopId)
        ? prev.filter((id) => id !== stopId)
        : [...prev, stopId]
    );
  };

  const handleConfirm = () => {
    if (!courierId) return;

    revertStopCompletion({
      variables: {
        courierId: courierId,
        shipmentId: shipment.id,
        stopIds: selectedStopIds,
      },
    })
      .then(() => {
        onSuccess();
        onClose();
      })
      .catch((error) => {
        showError(error);
      });
  };

  return (
    <BaseModal isOpen onClose={onClose} closeable size="default">
      <BaseModalHeader>Revert Completed Stops</BaseModalHeader>
      <BaseModalBody>
        {completedStops.length === 0 ? (
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded">
            <p className="text-yellow-800 text-sm">
              This shipment has no completed stops to revert.
            </p>
          </div>
        ) : (
          <p className="mb-4">Select the completed stops you want to revert:</p>
        )}
        <div className="space-y-2">
          {orderedStops.map((stop) => (
            <div
              key={stop.id}
              data-testid={`revert-stop-row-${stop.id}`}
              className={`flex items-center ${
                !stop.completedAt ? "opacity-50" : ""
              }`}
            >
              {stop.completedAt ? (
                <Checkbox
                  checked={selectedStopIds.includes(stop.id)}
                  onChange={() => handleToggleStop(stop.id)}
                />
              ) : (
                <Tooltip content="Not completed">
                  <div className="w-4 h-4 bg-gray-300 rounded-full flex items-center justify-center">
                    <XMarkIcon className="w-3 h-3 text-gray-500" />
                  </div>
                </Tooltip>
              )}
              <StopCard stop={stop} />
            </div>
          ))}
        </div>
        <div className="h-8 mt-2 mb-4">
          {selectedStopIds.length > 0 && (
            <p className="text-sm text-gray-500 text-center">
              Are you sure you want to revert the selected completed stops? This
              will mark them as incomplete.
            </p>
          )}
        </div>
      </BaseModalBody>
      <BaseModalFooter>
        <div className="flex justify-between border-t border-gray-200 pt-4">
          <CourieButton onClick={onClose} color="secondary">
            Cancel
          </CourieButton>
          <CourieButton
            onClick={handleConfirm}
            disabled={
              completedStops.length === 0 ||
              selectedStopIds.length === 0 ||
              revertStopCompletionLoading
            }
            isProcessing={revertStopCompletionLoading}
            outline={false}
            color="failure"
          >
            Revert Completed Stops
          </CourieButton>
        </div>
      </BaseModalFooter>
    </BaseModal>
  );
}

export default RevertStopCompletionModal;
